import {
  Anchor,
  Badge,
  Button,
  defaultVariantColorsResolver,
  type VariantColorsResolver,
  Drawer,
  Modal,
  Paper,
  PasswordInput,
  PinInput,
  Select,
  TextInput,
  Textarea,
  mergeThemeOverrides,
  type MantineThemeOverride,
} from "@mantine/core";
import { theme } from "./theme";

/**
 * The customer application's design language (M0.14 — *the look*).
 *
 * ## Why this is a second theme and not an edit to `theme.ts`
 *
 * `theme.ts` is shared: both applications import it, and it is where the colour arithmetic lives
 * that neither may diverge from. M0.14's scope is **the customer application only** — the host
 * panel is explicitly unchanged (`docs/design/ui-specification.md` §6, decision 17) — so applying
 * the brand typeface and the pill geometry to the shared theme would have redesigned the panel as a
 * side effect of styling the room.
 *
 * So the shared theme keeps every token both apps agree on, including the `flame` ramp, and
 * this file is the layer only the customer app mounts. Nothing here is a *different* answer from
 * `theme.ts`; it is a longer one.
 *
 * ## It is Mantine, configured
 *
 * There is no component library of our own and there is not going to be one. The design language is
 * tokens plus `Component.extend()` defaults, and everything component-specific stays in a
 * `*.module.scss` beside its component (arch §4.2). The single bespoke control in the whole design
 * is the swipe-to-bid track, because Mantine has no equivalent and composing one from `Slider`
 * would be a worse lie than writing it.
 */

/**
 * Gotham Narrow, from the Adobe Fonts kit `osp5uwi` loaded in `index.html`.
 *
 * **The fallback is not decoration.** Gotham Narrow is, as the name says, narrow — materially
 * narrower than any system face — so the moment between first paint and the webfont arriving is a
 * reflow of every line in the room unless the fallback is corrected to match its metrics. That
 * correction is the `@font-face` block in `theme.css` with `size-adjust`; this stack names it.
 *
 * The kit ships `font-display: auto`, which browsers treat as roughly `block` — up to three seconds
 * of *invisible* text. On a cold share link into a running show that is the lot title and the bid
 * amount blank while an auction runs. It has to be set to `swap` in the Adobe Fonts web project,
 * which is a setting on Adobe's side and cannot be asserted from here — and is still open under
 * *Carried forward* in `docs/implementation-plan.md`.
 */
const FONT =
  '"gotham-narrow", "Gotham Narrow Fallback", system-ui, -apple-system, sans-serif';

/**
 * Brand yellow as **text**, fixed at the one layer that actually holds.
 *
 * `theme.ts` records the arithmetic: `#ffd100` is `1.9:1` on white, and *even the darkest rung of
 * this ramp only reaches `4.1:1`* — under AA for normal text. `autoContrast` solves the case Mantine
 * derives, which is ink on a yellow **fill**. It does not solve the three variants where the brand
 * colour *is* the text:
 *
 *   light        a pale tint behind primary-coloured text
 *   subtle       no background at all, primary-coloured text
 *   outline      a primary border with primary-coloured text
 *
 * **A CSS variable override in `theme.css` looks like the fix and silently does nothing.** Mantine
 * generates `--mantine-color-peeko-light-color` at runtime and injects it into a `<style>` element
 * from `MantineProvider` — which lands in the document *after* a stylesheet link, so the
 * framework's value wins every time, and a `light` brand button renders at `#9e7900`, the ramp's
 * shade 9, at `4.1:1`. Worth remembering: overriding a Mantine-generated colour variable from a
 * static stylesheet is not a reliable mechanism.
 *
 * A variant resolver is the supported one. It runs inside Mantine's own resolution, so there is
 * nothing to out-specify, and it is narrowed to the brand palette — every other colour keeps
 * framework behaviour, so a red subtle button is still red.
 */
const variantColorResolver: VariantColorsResolver = (input) => {
  const resolved = defaultVariantColorsResolver(input);
  const brand = input.color === "peeko" || input.color === undefined;
  const readable =
    input.variant === "light" ||
    input.variant === "subtle" ||
    input.variant === "outline" ||
    input.variant === "transparent" ||
    input.variant === "default";

  if (!brand || !readable) return resolved;
  return {
    ...resolved,
    color: "var(--peeko-ink)",
    // `outline` draws its border from the same colour, and a yellow hairline round ink text is the
    // same failure wearing a different hat (WCAG 1.4.11 asks 3:1 of a control's visual boundary).
    border:
      input.variant === "outline" ? "1px solid var(--peeko-ink)" : resolved.border,
  };
};

export const customerTheme: MantineThemeOverride = mergeThemeOverrides(theme, {
  variantColorResolver,
  fontFamily: FONT,
  fontFamilyMonospace: "ui-monospace, SFMono-Regular, Menlo, monospace",

  headings: {
    fontFamily: FONT,
    fontWeight: "700",
    sizes: {
      h1: { fontSize: "2rem", lineHeight: "1.15", fontWeight: "700" },
      h2: { fontSize: "1.5rem", lineHeight: "1.2", fontWeight: "700" },
      h3: { fontSize: "1.25rem", lineHeight: "1.25", fontWeight: "700" },
      h4: { fontSize: "1.0625rem", lineHeight: "1.3", fontWeight: "700" },
      h5: { fontSize: "0.9375rem", lineHeight: "1.35", fontWeight: "700" },
      h6: { fontSize: "0.8125rem", lineHeight: "1.4", fontWeight: "700" },
    },
  },

  /**
   * Five steps, and one of them is doing unusual work.
   *
   * `xs` at 11px is smaller than a body scale normally goes, and it exists for one thing: the lot
   * bar's metadata — vendor, the shipping disclosure — sits under a title on a phone, over video,
   * competing with the picture for the same pixels. It is never used for anything a customer has to
   * read to buy. `R-A11Y-1` puts the product's full detail in the browser as real text, which is
   * what makes a line this small acceptable at all.
   */
  fontSizes: {
    xs: "0.6875rem",
    sm: "0.8125rem",
    md: "0.9375rem",
    lg: "1.0625rem",
    xl: "1.25rem",
  },

  lineHeights: {
    xs: "1.35",
    sm: "1.4",
    md: "1.5",
    lg: "1.45",
    xl: "1.35",
  },

  /**
   * `xl` is a pill, and that is the whole geometry of the design.
   *
   * Every button, badge and input in the comps is fully rounded; every card and panel is ~16px.
   * So `xl` is the pill and `lg` is the panel, and a component picks one rather than carrying a
   * pixel value in its stylesheet. `9999px` rather than `50%`, which would make a wide button an
   * ellipse instead of a stadium.
   */
  radius: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "0.75rem",
    lg: "1rem",
    xl: "9999px",
  },

  defaultRadius: "md",

  components: {
    /**
     * `R-A11Y-13` — *buying controls meet target-size minimums with adequate spacing.* `md` is
     * Mantine's 42px and `lg` is 50px; the room's buying controls take `lg` explicitly, and this
     * default raises everything else off the 36px `sm` that Mantine would otherwise give a button
     * on a phone.
     */
    Button: Button.extend({
      defaultProps: { radius: "xl", size: "md", fw: 600 },
    }),
    Badge: Badge.extend({
      defaultProps: { radius: "xl" },
    }),
    /*
      A link is text, and the brand yellow is a fill (`theme.ts`). Mantine colours an `Anchor` with
      the primary colour, which on this palette is `1.9:1` on white — a link nobody can see.
      `variantColorResolver` above fixes the `light`, `subtle` and `outline` variants; `Anchor`
      does not go through it, so it is set here.

      `underline="always"` rather than on hover, and not only for taste: with the colour now brand
      ink, an underline is the *only* thing distinguishing a link from surrounding text, and
      WCAG 1.4.1 forbids conveying that by colour alone.
    */
    Anchor: Anchor.extend({
      defaultProps: { c: "ink.8", underline: "always" },
    }),
    Paper: Paper.extend({
      defaultProps: { radius: "lg" },
    }),
    /*
      `yOffset` in `rem`, and that is a correction rather than a preference.

      Mantine's default is **`5vh`**, applied as padding above and below the dialog. Every sheet
      in this application also caps its own height, and the two add up — so a sheet capped at
      `85vh` would occupy `95vh`.

      On iOS `vh` is the viewport with the browser bars **hidden**, which is taller than what is
      visible while they are showing, so `95vh` there is comfortably more than the screen: the
      foot of the sheet — *Claim*, *Save*, *Add another card* — goes under the address bar or off
      the bottom. A `rem` offset cannot do that, and it pairs with the `dvh` caps the sheets use
      so the arithmetic closes exactly: `2rem + (100dvh - 4rem) + 2rem`.
    */
    /*
      Every sheet's title bold. Mantine sets them at body weight, which on a sheet with a bold
      field label under it reads as the title being the lesser of the two.
    */
    Modal: Modal.extend({
      defaultProps: { radius: "lg", centered: true, yOffset: "2rem" },
      styles: { title: { fontWeight: 700 } },
    }),
    Drawer: Drawer.extend({
      defaultProps: { radius: "lg" },
      styles: { title: { fontWeight: 700 } },
    }),
    TextInput: TextInput.extend({
      defaultProps: { radius: "xl", size: "md" },
    }),
    PasswordInput: PasswordInput.extend({
      defaultProps: { radius: "xl", size: "md" },
    }),
    Textarea: Textarea.extend({
      defaultProps: { radius: "md", size: "md" },
    }),
    Select: Select.extend({
      defaultProps: { radius: "xl", size: "md" },
    }),
    /**
     * The one-time code (§4.2). `oneTimeCode` is what makes iOS offer the code from the Messages
     * app above the keyboard, and Android autofill it — the difference between a customer reading
     * six digits off another screen and tapping once.
     */
    PinInput: PinInput.extend({
      defaultProps: {
        radius: "md",
        size: "lg",
        length: 6,
        oneTimeCode: true,
        type: /^[0-9]*$/,
      },
    }),
  },
});
